<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import apiClient from "@/apiClient.js";
import { useAuthStore } from "@/stores/AuthStore";
import { filterSearchResults, isSearchTargetAllowed } from "@/utils/searchAccess";
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
} from "@heroicons/vue/24/solid";

const router = useRouter();
const authStore = useAuthStore();
const query = ref("");
const loading = ref(false);
const errorMessage = ref("");
const results = ref({});
const summary = ref("");
const dropdownOpen = ref(false);
const primaryTarget = ref("");
const inputRef = ref(null);
const ticketResults = ref([]);
const ticketLoading = ref(false);
const ticketError = ref("");
const wrapper = ref(null);
const selectedItemIndex = ref(-1);
const searchResultElements = ref({});
const MIN_LENGTH = 2;
let debounceHandle = null;
let latestQueryId = 0;

const roles = computed(() => authStore.roles || []);
const canSearchTickets = computed(() => isSearchTargetAllowed("tickets", roles.value));

const scheduleSearch = (value) => {
  clearTimeout(debounceHandle);
  if (value.length < MIN_LENGTH) {
    resetResults();
    return;
  }
  dropdownOpen.value = true;
  const queryId = ++latestQueryId;
  ticketError.value = "";
  ticketResults.value = [];
  ticketLoading.value = canSearchTickets.value;
  debounceHandle = setTimeout(() => {
    performSearch(value, queryId);
    if (canSearchTickets.value) {
      performTicketSearch(value, queryId);
    }
  }, 350);
};

const performSearch = async (value, queryId) => {
  loading.value = true;
  errorMessage.value = "";
  try {
    const response = await apiClient.post("/api/search", { query: value });
    if (!response || !response.data) {
      throw new Error("No response");
    }
    if (queryId !== latestQueryId) {
      return;
    }
    results.value = filterSearchResults(response.data.results ?? {}, roles.value);
    summary.value = response.data.summary ?? "";
    primaryTarget.value = response.data.primary_target ?? "";
    dropdownOpen.value = true;
  } catch (error) {
    if (queryId !== latestQueryId) {
      return;
    }
    errorMessage.value = error?.response?.data?.message ?? "Search failed.";
    results.value = {};
    summary.value = "";
  } finally {
    if (queryId === latestQueryId) {
      loading.value = false;
    }
  }
};

const performTicketSearch = async (value, queryId) => {
  ticketLoading.value = true;
  ticketError.value = "";
  try {
    const response = await apiClient.post("/api/search/tickets", { query: value });
    if (queryId !== latestQueryId) {
      return;
    }
    ticketResults.value = response.data?.results ?? [];
  } catch (error) {
    if (queryId !== latestQueryId) {
      return;
    }
    ticketError.value = error?.response?.data?.message ?? "Ticket results unavailable.";
    ticketResults.value = [];
  } finally {
    if (queryId === latestQueryId) {
      ticketLoading.value = false;
    }
  }
};

const resetResults = () => {
  results.value = {};
  summary.value = "";
  errorMessage.value = "";
  primaryTarget.value = "";
  ticketResults.value = [];
  ticketError.value = "";
  ticketLoading.value = false;
  loading.value = false;
  dropdownOpen.value = false;
  selectedItemIndex.value = -1;
  searchResultElements.value = {};
};

const focusInput = () => {
  dropdownOpen.value = true;
  nextTick(() => {
    inputRef.value?.focus();
  });
};

defineExpose({
  focusInput,
});

watch(
  () => query.value.trim(),
  (value) => {
    if (!value) {
      resetResults();
      return;
    }
    scheduleSearch(value);
  },
);

const groupedResults = computed(() => {
  return Object.entries(results.value || {})
    .map(([target, entries]) => ({
      target,
      items: Array.isArray(entries) ? entries : [],
    }))
    .filter((group) => group.items.length > 0);
});

const groupedResultKey = (target, item) => `${target}-${item.id}`;
const ticketResultKey = (ticket) => `tickets-extra-${ticket.id}`;

const navigableResults = computed(() => {
  const groupedItems = groupedResults.value.flatMap((group) =>
    group.items.map((item) => ({
      key: groupedResultKey(group.target, item),
      target: group.target,
      item,
    })),
  );

  const asyncTickets = canSearchTickets.value
    ? ticketResults.value.map((ticket) => ({
      key: ticketResultKey(ticket),
      target: "tickets",
      item: ticket,
    }))
    : [];

  return [...groupedItems, ...asyncTickets];
});

const setSearchResultElement = (key, element) => {
  if (!key) {
    return;
  }
  if (element) {
    searchResultElements.value[key] = element;
    return;
  }
  delete searchResultElements.value[key];
};

const isSelectedResult = (key) =>
  selectedItemIndex.value >= 0 &&
  navigableResults.value[selectedItemIndex.value]?.key === key;

watch(
  () => navigableResults.value.map((entry) => entry.key),
  () => {
    selectedItemIndex.value = -1;
    searchResultElements.value = {};
  },
);

watch(selectedItemIndex, async (index) => {
  if (index < 0) {
    return;
  }
  const selectedResult = navigableResults.value[index];
  if (!selectedResult) {
    return;
  }
  await nextTick();
  searchResultElements.value[selectedResult.key]?.scrollIntoView({ block: "nearest" });
});

const resultDestination = (target, item) => {
  switch (target) {
    case "customers":
      return { name: "customer-view", params: { id: item.id } };
    case "contacts":
      return { name: "contact-view", params: { id: item.id } };
    case "invoices":
      return { name: "invoice-view", params: { id: item.id } };
    case "references":
      return { name: "reference-view", params: { id: item.id } };
    case "tickets":
      return { name: "tickets-view", params: { id: item.id } };
    case "staff":
      return { name: "staff-view", params: { id: item.id } };
    case "vehicles":
      return item?.id
        ? { name: "vehicles-view", params: { id: item.id } }
        : { name: "dashboard-fleet" };
    default:
      return null;
  }
};

const selectResult = (target, item) => {
  const destination = resultDestination(target, item);
  if (!destination) {
    return;
  }
  router.push(destination);
  query.value = "";
  resetResults();
};

const handleEnter = () => {
  if (!navigableResults.value.length) {
    return;
  }
  const index = selectedItemIndex.value >= 0 ? selectedItemIndex.value : 0;
  const selectedResult = navigableResults.value[index];
  if (selectedResult) {
    selectResult(selectedResult.target, selectedResult.item);
  }
};

const handleArrowDown = () => {
  if (!navigableResults.value.length) {
    return;
  }
  selectedItemIndex.value = Math.min(
    selectedItemIndex.value + 1,
    navigableResults.value.length - 1,
  );
};

const handleArrowUp = () => {
  if (!navigableResults.value.length) {
    return;
  }
  selectedItemIndex.value = Math.max(selectedItemIndex.value - 1, 0);
};

const handleEscape = () => {
  resetResults();
  query.value = "";
};

const handleGlobalKeys = (event) => {
  const targetTag = (event.target?.tagName || "").toLowerCase();
  const typingField =
    targetTag === "input" ||
    targetTag === "textarea" ||
    event.target?.isContentEditable;

  if (
    event.key === "/" &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.altKey &&
    !typingField
  ) {
    event.preventDefault();
    focusInput();
  }

  if (event.key === "Escape" && dropdownOpen.value) {
    event.preventDefault();
    resetResults();
  }
};

const targetLabel = (target) => {
  return {
    customers: "Customers",
    contacts: "Contacts",
    invoices: "Invoices",
    references: "Jobs",
    tickets: "Tickets",
    staff: "Staff",
    vehicles: "Vehicles",
  }[target] ?? target;
};

const prettyLabel = (target, item) => {
  switch (target) {
    case "customers":
      return item.company ?? `Customer #${item.id}`;
    case "contacts":
      return item.name ?? `Contact #${item.id}`;
    case "invoices":
      return item.code ?? `Invoice #${item.id}`;
    case "references":
      return item.code ?? item.name ?? `Reference #${item.id}`;
    case "tickets":
      return `Ticket #${item.id}`;
    case "staff":
      return item.name ?? `Staff #${item.id}`;
    case "vehicles":
      return item.registration ?? `Vehicle #${item.id}`;
    default:
      return `Result #${item.id}`;
  }
};

const prettySubLabel = (target, item) => {
  switch (target) {
    case "customers":
      return item.contact?.email ?? item.contact?.name ?? "";
    case "invoices":
      return [item.customer, item.status].filter(Boolean).join(" • ");
    case "references":
      return [item.customer, item.name].filter(Boolean).join(" • ");
    case "tickets":
      return item.subject ?? item.contact?.email ?? "";
    case "staff":
      return item.email ?? "";
    case "vehicles":
      return [item.make, item.model].filter(Boolean).join(" ");
    default:
      return "";
  }
};

const handleClickOutside = (event) => {
  if (!wrapper.value?.contains(event.target)) {
    resetResults();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("keydown", handleGlobalKeys);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("keydown", handleGlobalKeys);
});
</script>

<template>
  <div class="relative" ref="wrapper">
    <div class="relative">
      <MagnifyingGlassIcon class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
      <input
        ref="inputRef"
        v-model="query"
        type="text"
        class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white/90 dark:bg-slate-800/80 px-9 py-2 text-sm text-slate-800 placeholder-slate-400 shadow focus:ring-2 focus:ring-indigo-400 dark:text-slate-100 dark:placeholder-slate-400"
        placeholder="Search customers, invoices, jobs..."
        @keydown.down.prevent="handleArrowDown"
        @keydown.up.prevent="handleArrowUp"
        @keydown.enter.prevent="handleEnter"
        @keydown.esc.stop.prevent="handleEscape"
        autocomplete="off"
      />
      <ArrowPathIcon
        v-if="loading"
        class="w-4 h-4 text-indigo-500 absolute right-3 top-1/2 -translate-y-1/2 animate-spin"
      />
    </div>
    <div
      v-if="dropdownOpen"
      class="absolute z-30 mt-2 w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl max-h-96 overflow-y-auto"
    >
      <div class="p-3 border-b border-slate-100 dark:border-slate-700 text-xs text-slate-500 space-y-1">
        <p v-if="errorMessage">{{ errorMessage }}</p>
        <p v-else-if="summary">{{ summary }}</p>
        <p v-else>Type to search across available records.</p>
      </div>
      <template v-if="groupedResults.length && !errorMessage">
        <section
          v-for="group in groupedResults"
          :key="group.target"
          class="border-b border-slate-100 dark:border-slate-700 last:border-b-0"
        >
          <div class="px-4 py-2 text-xs uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <span class="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
              {{ targetLabel(group.target) }}
            </span>
            <span v-if="group.items.length" class="text-slate-400">
              {{ group.items.length }} result{{ group.items.length === 1 ? "" : "s" }}
            </span>
          </div>
          <ul>
            <li
              v-for="item in group.items"
              :key="`${group.target}-${item.id}`"
              :ref="(element) => setSearchResultElement(groupedResultKey(group.target, item), element)"
              :class="[
                'px-4 py-3 cursor-pointer border-t border-slate-50 dark:border-slate-700 transition',
                isSelectedResult(groupedResultKey(group.target, item))
                  ? 'bg-slate-100 dark:bg-slate-700'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-700',
              ]"
              @click="selectResult(group.target, item)"
            >
              <p class="text-sm font-medium text-slate-800 dark:text-slate-100 flex items-center justify-between">
                <span>{{ prettyLabel(group.target, item) }}</span>
                <span class="text-[11px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200">
                  {{ targetLabel(group.target) }}
                </span>
              </p>
              <p class="text-xs text-slate-500" v-if="prettySubLabel(group.target, item)">
                {{ prettySubLabel(group.target, item) }}
              </p>
            </li>
          </ul>
        </section>
      </template>
      <div v-else-if="!loading && !errorMessage" class="p-4 text-sm text-slate-500">
        No matches yet—keep typing to refine.
      </div>
      <section v-if="canSearchTickets" class="border-t border-slate-100 dark:border-slate-700">
        <div class="px-4 py-2 text-xs uppercase tracking-wider text-slate-500 flex items-center gap-2">
          <span>Tickets</span>
          <ArrowPathIcon
            v-if="ticketLoading"
            class="w-4 h-4 text-indigo-500 animate-spin"
          />
        </div>
        <div class="px-4 pb-4 text-sm text-slate-600 dark:text-slate-200 space-y-3">
          <p v-if="ticketError" class="text-xs text-red-500">{{ ticketError }}</p>
          <ul v-else-if="ticketResults.length" class="space-y-2">
            <li
              v-for="ticket in ticketResults"
              :key="ticketResultKey(ticket)"
              :ref="(element) => setSearchResultElement(ticketResultKey(ticket), element)"
              :class="[
                'px-3 py-2 rounded cursor-pointer transition',
                isSelectedResult(ticketResultKey(ticket))
                  ? 'bg-slate-100 dark:bg-slate-600'
                  : 'bg-slate-50 dark:bg-slate-700/60 hover:bg-slate-100 dark:hover:bg-slate-600',
              ]"
              @click="selectResult('tickets', ticket)"
            >
              <p class="text-sm font-medium text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <span class="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200 text-[11px]">
                  Ticket #{{ ticket.id }}
                </span>
                <span>{{ ticket.subject ?? 'View ticket' }}</span>
              </p>
              <p class="text-xs text-slate-500" v-if="prettySubLabel('tickets', ticket)">
                {{ prettySubLabel('tickets', ticket) }}
              </p>
            </li>
          </ul>
          <p v-else class="text-xs text-slate-500">
            Tickets load after a second—keep typing to refine.
          </p>
        </div>
      </section>
    </div>
  </div>
</template>
